import { useMemo, useState } from 'react';
import {
  type InvoiceMatchResponse,
  type PartDocType,
  type PartDocument,
  type PartRequestWithContext,
  type UpdatePartRequestInput,
} from '@etnrlz/shared';
import { useMatchInvoice, usePartRequests, useUpdatePartRequestAny } from '../../lib/queries';
import { Button, Select, Spinner, cx } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { Icon } from '../../components/icons';
import { formatMoney } from '../../lib/format';
import { fileToBase64, uploadFile } from '../../lib/upload';

type Phase = 'analyzing' | 'ready' | 'error' | 'applying' | 'done';

interface ItemRow {
  descricao: string;
  valor?: number;
  quantidade?: number;
  confianca: 'alta' | 'media' | 'baixa' | 'nenhuma';
  motivo?: string;
  /** `${osId}|${partId}` da peça escolhida, ou '' para não anexar. */
  selKey: string;
}

interface FileEntry {
  key: string;
  file: File;
  nome: string;
  phase: Phase;
  tipoDoc: PartDocType;
  result?: InvoiceMatchResponse;
  items: ItemRow[];
  error?: string;
  attached?: number;
}

const DOC_TIPOS: { value: PartDocType; label: string }[] = [
  { value: 'INVOICE', label: 'Invoice (fornecedor)' },
  { value: 'NOTA_FISCAL', label: 'Nota fiscal (BR)' },
  { value: 'OUTRO', label: 'Outro' },
];

const CONF: Record<ItemRow['confianca'], { label: string; color: string }> = {
  alta: { label: 'alta', color: '#34d399' },
  media: { label: 'média', color: '#fbbf24' },
  baixa: { label: 'baixa', color: '#f87171' },
  nenhuma: { label: 'sem match', color: '#8a93a2' },
};

const partKey = (osId: string, partId: string) => `${osId}|${partId}`;

/**
 * Sobe invoices/NF, usa o Opus 4.8 para casar cada item com as peças
 * cadastradas e mostra uma tela de revisão para o usuário aprovar/ajustar antes
 * de anexar o arquivo às peças escolhidas (preenchendo campos vazios).
 */
export function InvoiceReviewModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: parts } = usePartRequests();
  const match = useMatchInvoice();
  const update = useUpdatePartRequestAny();
  const [files, setFiles] = useState<FileEntry[]>([]);

  const partsByKey = useMemo(() => {
    const m = new Map<string, PartRequestWithContext>();
    for (const p of parts ?? []) m.set(partKey(p.osId, p.id), p);
    return m;
  }, [parts]);

  const patch = (key: string, p: Partial<FileEntry>) =>
    setFiles((prev) => prev.map((f) => (f.key === key ? { ...f, ...p } : f)));

  const setItemSel = (fileKey: string, idx: number, selKey: string) =>
    setFiles((prev) =>
      prev.map((f) =>
        f.key === fileKey
          ? { ...f, items: f.items.map((it, i) => (i === idx ? { ...it, selKey } : it)) }
          : f,
      ),
    );

  const close = () => {
    setFiles([]);
    onClose();
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list?.length) return;
    const picked = Array.from(list);
    e.target.value = '';
    const created: FileEntry[] = picked.map((file) => ({
      key: crypto.randomUUID(),
      file,
      nome: file.name,
      phase: 'analyzing',
      tipoDoc: 'INVOICE',
      items: [],
    }));
    setFiles((prev) => [...prev, ...created]);
    for (const entry of created) {
      if (entry.file.size > 4_500_000) {
        patch(entry.key, { phase: 'error', error: 'Arquivo grande demais (máx. ~4,5 MB).' });
        continue;
      }
      try {
        const { data, mediaType } = await fileToBase64(entry.file);
        const r = await match.mutateAsync({ data, mediaType, nome: entry.nome });
        const items: ItemRow[] = (r.itens ?? []).map((it) => ({
          descricao: it.descricao,
          valor: it.valor,
          quantidade: it.quantidade,
          confianca: it.confianca,
          motivo: it.motivo,
          selKey: it.partId && it.osId ? partKey(it.osId, it.partId) : '',
        }));
        patch(entry.key, { phase: 'ready', result: r, items, tipoDoc: r.tipoDoc });
      } catch {
        patch(entry.key, { phase: 'error', error: 'Não consegui ler o documento.' });
      }
    }
  };

  const applyAll = async () => {
    for (const entry of files) {
      if (entry.phase !== 'ready') continue;
      const selected = entry.items.filter((it) => it.selKey);
      if (!selected.length) continue;
      patch(entry.key, { phase: 'applying' });
      try {
        const url = await uploadFile(entry.file, 'part-requests/docs');
        // Uma invoice pode cobrir várias peças — anexa o arquivo em cada uma.
        const distinct = new Map<string, ItemRow>();
        for (const it of selected) if (!distinct.has(it.selKey)) distinct.set(it.selKey, it);
        let attached = 0;
        for (const [key, it] of distinct) {
          const part = partsByKey.get(key);
          if (!part) continue;
          const [osId, prId] = key.split('|');
          const doc: PartDocument = { tipo: entry.tipoDoc, nome: entry.nome, url };
          const input: UpdatePartRequestInput = {
            documentos: [...(part.documentos ?? []), doc],
          };
          // Preenche campos vazios (não sobrescreve o que já existe).
          if (!part.orderId && entry.result?.orderId) input.orderId = entry.result.orderId;
          if (part.valor == null && it.valor != null) {
            input.valor = it.valor;
            if (entry.result?.moeda) input.moeda = entry.result.moeda;
          }
          await update.mutateAsync({ osId: osId!, prId: prId!, input });
          attached += 1;
        }
        patch(entry.key, { phase: 'done', attached });
      } catch {
        patch(entry.key, { phase: 'error', error: 'Falha ao anexar.' });
      }
    }
  };

  const busy =
    match.isPending || files.some((f) => f.phase === 'analyzing' || f.phase === 'applying');
  const canApply =
    !busy && files.some((f) => f.phase === 'ready' && f.items.some((i) => i.selKey));
  const allDone = files.length > 0 && files.every((f) => f.phase === 'done' || f.phase === 'error');

  return (
    <Modal
      open={open}
      onClose={close}
      wide
      title="Subir invoices / notas fiscais"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {allDone ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button onClick={applyAll} disabled={!canApply}>
            {busy ? <Spinner className="h-4 w-4" /> : <Icon name="plus" className="h-4 w-4" />}
            Aprovar e anexar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="flex items-center justify-center gap-2 text-sm px-3 py-3 rounded-lg bg-surface-2 border border-dashed border-border-strong hover:border-accent cursor-pointer transition-colors">
          <Icon name="plus" className="h-4 w-4" />
          Escolher arquivos (PDF ou imagem) — pode selecionar vários
          <input
            type="file"
            accept="application/pdf,image/*"
            multiple
            className="hidden"
            onChange={onPick}
          />
        </label>

        <p className="text-xs text-muted">
          A IA (Opus 4.8) lê cada documento, identifica os itens e sugere em qual peça anexar.
          Revise os casamentos abaixo — os de <span style={{ color: CONF.baixa.color }}>confiança
          baixa</span> ou <span style={{ color: CONF.nenhuma.color }}>sem match</span> merecem
          atenção. Ajuste e clique em “Aprovar e anexar”.
        </p>

        {files.length === 0 && (
          <p className="text-sm text-muted text-center py-6">Nenhum arquivo selecionado ainda.</p>
        )}

        {files.map((f) => (
          <FileCard key={f.key} entry={f} parts={parts ?? []} onTipo={(t) => patch(f.key, { tipoDoc: t })} onSel={(i, k) => setItemSel(f.key, i, k)} />
        ))}
      </div>
    </Modal>
  );
}

function FileCard({
  entry,
  parts,
  onTipo,
  onSel,
}: {
  entry: FileEntry;
  parts: PartRequestWithContext[];
  onTipo: (t: PartDocType) => void;
  onSel: (idx: number, selKey: string) => void;
}) {
  const r = entry.result;
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-medium text-sm truncate flex items-center gap-2">
          <Icon name="tag" className="h-4 w-4 text-muted shrink-0" />
          <span className="truncate">{entry.nome}</span>
        </div>
        {entry.phase === 'analyzing' && (
          <span className="text-xs text-muted inline-flex items-center gap-1">
            <Spinner className="h-3 w-3" /> lendo…
          </span>
        )}
        {entry.phase === 'applying' && (
          <span className="text-xs text-muted inline-flex items-center gap-1">
            <Spinner className="h-3 w-3" /> anexando…
          </span>
        )}
        {entry.phase === 'done' && (
          <span className="text-xs" style={{ color: CONF.alta.color }}>
            ✓ anexado em {entry.attached} peça(s)
          </span>
        )}
        {entry.phase === 'error' && (
          <span className="text-xs text-st-danger">{entry.error}</span>
        )}
      </div>

      {r && (entry.phase === 'ready' || entry.phase === 'applying' || entry.phase === 'done') && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted mb-2">
            <div className="inline-flex items-center gap-1">
              <span>Tipo:</span>
              <Select
                className="py-0.5 text-xs"
                value={entry.tipoDoc}
                disabled={entry.phase !== 'ready'}
                onChange={(e) => onTipo(e.target.value as PartDocType)}
              >
                {DOC_TIPOS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </Select>
            </div>
            {r.orderId && <span className="num">pedido #{r.orderId}</span>}
            {r.fornecedor && <span>· {r.fornecedor}</span>}
            {r.valorTotal != null && (
              <span className="num">· total {formatMoney(r.valorTotal, r.moeda)}</span>
            )}
          </div>

          <ul className="space-y-2">
            {entry.items.map((it, i) => {
              const conf = CONF[it.confianca];
              return (
                <li key={i} className="rounded-lg bg-surface-2/60 border border-border p-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="text-sm truncate">
                      {it.descricao}
                      {it.valor != null && (
                        <span className="num text-muted text-xs ml-2">{formatMoney(it.valor)}</span>
                      )}
                    </div>
                    <span
                      className="text-xs shrink-0 px-1.5 py-0.5 rounded"
                      style={{ color: conf.color, background: `${conf.color}1a` }}
                    >
                      {conf.label}
                    </span>
                  </div>
                  <Select
                    className={cx('py-1 text-sm', !it.selKey && 'text-muted')}
                    value={it.selKey}
                    disabled={entry.phase !== 'ready'}
                    onChange={(e) => onSel(i, e.target.value)}
                  >
                    <option value="">— não anexar —</option>
                    {parts.map((p) => (
                      <option key={partKey(p.osId, p.id)} value={partKey(p.osId, p.id)}>
                        {p.titulo ?? 'Peça'} · {p.osTitulo}
                      </option>
                    ))}
                  </Select>
                  {it.motivo && <p className="text-xs text-muted mt-1">{it.motivo}</p>}
                </li>
              );
            })}
            {entry.items.length === 0 && (
              <li className="text-xs text-muted">Nenhum item identificado no documento.</li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}
